/**
 * Side effects after the user confirms a campus in the course-page picker.
 * Always updates campus state. Auto-selects the owning modality only when that
 * modality is available at the confirmed campus (WEL-1696).
 *
 * `offeringCampusIds`: campus slugs listed on the modality's course offerings.
 * An empty list means no campus restriction (available everywhere) — same rule
 * as Select enablement / unavailable price lines.
 */
export type CampusConfirmationInput = {
	campusId: string;
	modalityId?: string | null;
	modalitySlug?: string | null;
	courseSlug?: string | null;
	offeringCampusIds?: readonly string[];
};

export type CampusConfirmationHandlers = {
	setSelectedCampusId: (campusId: string) => void;
	setCampusResolutionSettled: (settled: boolean) => void;
	setSelectedLearningFormat: (modalityId: string | null) => void;
	setSessionModalitySlug: (slug: string | null) => void;
	trackModalitySelected: (properties: {
		course: string;
		modality: string;
		auto_select: boolean;
	}) => void;
};

/** True when the modality has no campus list, or the campus is in that list. */
export function isModalityAvailableAtCampus(
	campusId: string,
	offeringCampusIds: readonly string[] = []
): boolean {
	return offeringCampusIds.length === 0 || offeringCampusIds.includes(campusId);
}

export function applyCampusConfirmation(
	input: CampusConfirmationInput,
	handlers: CampusConfirmationHandlers
): void {
	handlers.setSelectedCampusId(input.campusId);
	handlers.setCampusResolutionSettled(true);

	if (!input.modalityId || !input.modalitySlug) {
		return;
	}

	if (
		!isModalityAvailableAtCampus(input.campusId, input.offeringCampusIds ?? [])
	) {
		return;
	}

	handlers.setSelectedLearningFormat(input.modalityId);
	handlers.setSessionModalitySlug(input.modalitySlug);
	handlers.trackModalitySelected({
		course: input.courseSlug ?? "",
		modality: input.modalitySlug,
		auto_select: true,
	});
}
